import React, { useState, useRef, useEffect } from 'react'
import Input from 'components/input'
import Menu, { MenuProps } from 'components/menu'
import {
  Form,
  Chart,
  Pencil,
  Save,
  Cross,
  Delete,
  Spreadsheet,
  TransposeView,
  MatrixView,
  CalendarView,
  Dashboard,
  Plus,
  SwitchView,
  Tick
} from 'components/icons'
import { ITableViewWithColumns } from 'types'
import { ViewTypes } from 'components/spreadsheet/constants/const'
import { getViewCapabilities } from 'components/spreadsheet/constants/viewCapabilities'
import { addNewView, updateView } from 'components/spreadsheet/contexts/data/actions'
import { useApplicationStore } from 'hooks/application'
import { useProject } from 'hooks/useProject'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import {
  MenuFooter,
  MenuItem,
  MenuHeader,
  MenuLabel,
  MenuList,
  MenuSearch,
  MenuSeparator,
  MenuSub,
  menuItem
} from 'components/menu/items'
import Tooltip from 'components/tooltip'
import { useAuth } from 'hooks/auth'
import usePersonalSaveBlocked, { getSaveBlockedTooltip } from 'components/spreadsheet/hooks/usePersonalSaveBlocked'
import { useNavigate, generatePath } from 'react-router-dom'
import { TABLE } from 'routes'
import { groupViews, useSelectView } from 'components/spreadsheet/hooks/useViewSwitching'
import ViewSettingsItems, { hasViewSettings } from './viewSettings'

interface ViewRowProps {
  view: ITableViewWithColumns
  inProcess: boolean
  isSelected: boolean
  isEditing: boolean
  editedName: string
  indented?: boolean
  editInputRef: React.RefObject<HTMLInputElement | null>
  anyRowHydrating: boolean
  onSelect: () => void
  onStartEdit: () => void
  onSaveEdit: () => void
  onCancelEdit: () => void
  onEditNameChange: (value: string) => void
  onDelete: () => void
  canEdit: boolean
}

// Per-view-type presentation: the icon used for the view in the view list and
// the table page's tabs. Single source of truth — adding a new ViewType means
// adding one entry here. Icons take their label's colour: a colour per type
// was arbitrary, and meant nothing anyone chose.
export const VIEW_ROW_PRESENTATION: Record<number, { icon: React.ReactNode }> = {
  [ViewTypes.CUSTOM_VIEW]: { icon: <Dashboard /> },
  [ViewTypes.SPREADSHEET]: { icon: <Spreadsheet /> },
  [ViewTypes.FORM]: { icon: <Form /> },
  [ViewTypes.CHART]: { icon: <Chart /> },
  [ViewTypes.TRANSPOSE]: { icon: <TransposeView /> },
  [ViewTypes.CALENDAR]: { icon: <CalendarView /> },
  [ViewTypes.MATRIX]: { icon: <MatrixView /> }
}

// The list of views (All views, "N more", Switch View) is as wide as its longest
// name needs, from the width it had before up to a cap, past which names truncate.
// Menu still keeps it inside a narrow screen.
export const VIEW_LIST_MIN_WIDTH = 280
export const VIEW_LIST_MAX_WIDTH = 500

// Marks a view type that's still in beta, after its name in the new-view list: a small, quiet pill.
const BetaBadge: React.FC = () => (
  <span className="ml-1.5 rounded-full px-1.5 py-px align-[1px] text-[10px] font-medium leading-none bg-body text-surface">
    Beta
  </span>
)

export const DEFAULT_VIEW_ROW_PRESENTATION = VIEW_ROW_PRESENTATION[ViewTypes.TRANSPOSE]

// A button at a view row's end (rename, delete; cancel and save while renaming): a
// tab stop that Enter and Space press, without also choosing the row.
export const RowAction: React.FC<{
  label: string
  onPress: () => void
  className?: string
  children: React.ReactNode
}> = ({ label, onPress, className, children }) => (
  <div
    role="button"
    tabIndex={0}
    aria-label={label}
    title={label}
    className={`flex items-center justify-center size-6 rounded text-muted hover:text-body hover:bg-surface-strong/40 focus-visible:outline-2 focus-visible:outline-blue ${className ?? ''}`}
    onClick={(event) => {
      event.stopPropagation()
      onPress()
    }}
    onKeyDown={(event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      event.stopPropagation()
      onPress()
    }}
  >
    {children}
  </div>
)

// Holds an icon level with the first line of a row's name (its 20px line height).
const FirstLine: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <span className={`flex h-5 shrink-0 items-center self-start ${className ?? ''}`}>{children}</span>
)

// A view in the list, as a menu row: its icon and name, a tick when it's the
// open one, and (for views you can edit) rename and delete at its end on hover,
// in the tick's place. A name too long for the menu wraps to a second line, then
// truncates; the icon, tick and actions stay level with its first line.
const ViewRow: React.FC<ViewRowProps> = ({
  view,
  inProcess,
  isSelected,
  isEditing,
  editedName,
  indented = false,
  editInputRef,
  anyRowHydrating,
  onSelect,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onEditNameChange,
  onDelete,
  canEdit
}) => {
  const { icon } = VIEW_ROW_PRESENTATION[view.type] ?? DEFAULT_VIEW_ROW_PRESENTATION
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSaveEdit()
    } else if (e.key === 'Escape') {
      onCancelEdit()
    }
  }

  const select = () => {
    if (!isEditing && !anyRowHydrating) onSelect()
  }

  return (
    <div
      data-testid={indented ? 'personal-view-option' : 'view-context-option'}
      role="menuitemradio"
      aria-checked={isSelected}
      tabIndex={0}
      className={menuItem({ class: `group/row ${indented ? 'pl-7' : ''}` })}
      onClick={select}
      onKeyDown={(event) => {
        if (isEditing || event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return
        event.preventDefault()
        select()
      }}
    >
      <FirstLine>{icon}</FirstLine>
      {isEditing ? (
        <>
          <Input
            ref={editInputRef}
            type="text"
            value={editedName}
            onChange={(e) => onEditNameChange(e.target.value)}
            onBlur={onSaveEdit}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 h-7 py-0 px-2"
          />
          <RowAction label="Cancel" onPress={onCancelEdit}>
            <Cross />
          </RowAction>
          <RowAction label="Save" onPress={onSaveEdit}>
            <Save />
          </RowAction>
        </>
      ) : (
        <>
          <span data-testid="view-name" className="min-w-0 flex-1 whitespace-normal break-words line-clamp-2">
            {view.name}
            {view.isPersonalView && !inProcess && (
              <span className="text-muted font-normal"> {view.personalViewOwner?.email}</span>
            )}
          </span>
          {canEdit && (
            <div className="hidden group-hover/row:flex group-focus-within/row:flex items-center self-start -my-0.5">
              <RowAction label="Rename view" onPress={onStartEdit}>
                <Pencil />
              </RowAction>
              <RowAction label="Delete view" onPress={onDelete} className="hover:text-red">
                <Delete />
              </RowAction>
            </div>
          )}
          {isSelected && (
            <FirstLine className={canEdit ? 'group-hover/row:hidden group-focus-within/row:hidden' : ''}>
              <Tick className="text-body" aria-label="Open" />
            </FirstLine>
          )}
        </>
      )}
    </div>
  )
}

interface ViewMenuProps extends MenuProps {
  onDeleteView: (viewId: string, viewName: string) => void
  /** Only the New … View choices, for the table page's + New view button (its tabs list the views). */
  createOnly?: boolean
  /**
   * Opened from a view's tab on the table page: that view's settings (renaming,
   * who can add, import or delete rows) lead; switching to another view and
   * creating one sit in submenus, and deleting the view comes last.
   */
  withCurrentView?: boolean
  /** The view the This view section is about, if not the open one (its tab was right-clicked). */
  targetViewId?: string
  /** Starts renaming a view in its tab. */
  onRenameView?: (viewId: string) => void
}

const ViewMenu: React.FC<ViewMenuProps> = ({
  id,
  menuState,
  setMenuState,
  width,
  minWidth,
  onDeleteView,
  createOnly = false,
  withCurrentView = false,
  targetViewId,
  onRenameView
}) => {
  const { spreadsheetData, setSpreadsheetData, inProcess, originalViewId } = useDataContext()
  const [isCreatingNewViewType, setIsCreatingNewViewType] = useState<ViewTypes | null>(null)

  const navigate = useNavigate()
  const [search, setSearch] = useState<string>('')
  const [editingViewId, setEditingViewId] = useState<string | null>(null)
  const [editedName, setEditedName] = useState<string>('')
  const { selectView: switchToView, hydratingViewId } = useSelectView()
  const editInputRef = useRef<HTMLInputElement>(null)
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const { project } = useProject()
  const { user } = useAuth()

  useEffect(() => {
    if (editingViewId && editInputRef.current) {
      editInputRef.current.focus()
      editInputRef.current.select()
    }
  }, [editingViewId])

  const createNewViewSuccess = (newViewId: string) => {
    setIsCreatingNewViewType(null)
    setSnackbarMessage({ status: 'success', message: 'View created successfully!' })
    if (!inProcess) {
      navigate(
        generatePath(TABLE, {
          projectId: project.publicId,
          tableId: spreadsheetData.tableDetails.publicId,
          viewId: newViewId
        }),
        {
          replace: true
        }
      )
    }

    setMenuState({
      ...INITIAL_CONTEXT_MENU_STATE
    })
  }

  const createNewViewFailure = (error: any) => {
    setIsCreatingNewViewType(null)
    displayErrorMessage(error)
  }

  const createNewView = (type: number) => {
    if (inProcess && !originalViewId) {
      displayErrorMessage(
        "A personal view can only be made from an existing view, and this table's view couldn't be found. Refresh the page and try again."
      )
      return
    }

    const configurationParentId = inProcess && originalViewId ? originalViewId : null
    setIsCreatingNewViewType(type)
    addNewView(
      project.publicId,
      spreadsheetData,
      setSpreadsheetData,
      type,
      configurationParentId,
      createNewViewSuccess,
      createNewViewFailure,
      inProcess
    )
  }

  const startEditing = (viewId: string, currentName: string) => {
    setEditingViewId(viewId)
    setEditedName(currentName)
  }

  const saveViewName = (viewId: string) => {
    if (
      editedName.trim() &&
      editedName !== spreadsheetData.tableView.find((v: ITableViewWithColumns) => v.publicId === viewId)?.name
    ) {
      updateView(
        'name',
        spreadsheetData,
        setSpreadsheetData,
        viewId,
        editedName.trim(),
        project.publicId,
        () => {
          setSnackbarMessage({ status: 'success', message: 'View name updated!' })
        },
        (error: string) => {
          displayErrorMessage(error)
        }
      )
    }
    setEditingViewId(null)
    setEditedName('')
  }

  const { parentViews, personalViewsByParent } = groupViews(
    spreadsheetData.tableView,
    inProcess,
    originalViewId,
    search,
    'name'
  )
  const noResults = parentViews.length === 0 && Object.keys(personalViewsByParent).length === 0

  const canEditView = (view: ITableViewWithColumns): boolean => {
    if (view.isDefault) return false
    if (spreadsheetData.isAdmin) return true
    return view.isPersonalView && view.personalViewOwner?.publicId === user?.publicId
  }

  const selectView = async (viewId: string) => {
    if (await switchToView(viewId)) setMenuState({ ...INITIAL_CONTEXT_MENU_STATE })
  }

  const cancelEdit = () => {
    setEditingViewId(null)
    setEditedName('')
  }

  const handleDeleteView = (viewId: string, viewName: string) => {
    setMenuState({ ...INITIAL_CONTEXT_MENU_STATE })
    onDeleteView(viewId, viewName)
  }

  // The view the menu is about: the open one, unless another's tab was right-clicked (then from the table's
  // list of views).
  const sectionView =
    (targetViewId !== spreadsheetData.viewDetails.publicId &&
      spreadsheetData.tableView.find((view) => view.publicId === targetViewId)) ||
    spreadsheetData.viewDetails
  const aboutOpenView = sectionView.publicId === spreadsheetData.viewDetails.publicId
  // On the table page, a view's tab leads with the view itself; in a document, and from "N more", the menu is
  // the list of views.
  const aboutView = withCurrentView && !createOnly && !inProcess
  const canRenameSectionView = !!onRenameView && canEditView(sectionView)
  // The row settings of a spreadsheet or a Custom View.
  const hasSettings =
    canRenameSectionView ||
    (hasViewSettings(spreadsheetData) &&
      (sectionView.type === ViewTypes.SPREADSHEET || sectionView.type === ViewTypes.CUSTOM_VIEW))
  const canDeleteSectionView = hasViewSettings(spreadsheetData) || canEditView(sectionView)
  // Switching and creating belong to the open view's tab, or to a menu that would otherwise be empty.
  const showSubmenus = aboutOpenView || (!hasSettings && !canDeleteSectionView)

  // Form and Custom Views are owner-built: no personal views are made from them.
  const allowPersonalViews = !inProcess || getViewCapabilities(spreadsheetData.viewDetails.type).personalViews
  const personalViewsDisabled = usePersonalSaveBlocked()

  const wrapIfBlocked = (button: React.ReactElement): React.ReactElement =>
    personalViewsDisabled ? (
      <Tooltip content={getSaveBlockedTooltip(inProcess)}>
        <span className="inline-block w-full">{button}</span>
      </Tooltip>
    ) : (
      button
    )

  const viewCount = spreadsheetData.tableView.length
  // A long list gets a search; a submenu of a handful of views doesn't need one.
  const viewList = ({ editable, searchable }: { editable: boolean; searchable: boolean }) => (
    <>
      {searchable && (
        <MenuSearch
          placeholder="Search Views"
          aria-label="Search Views"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value.toLowerCase())}
        />
      )}
      <div role="menu" aria-label="Views" className="flex max-h-[280px] flex-col overflow-y-auto overflow-x-hidden p-1">
        {parentViews.map((tableView: ITableViewWithColumns) => {
          const childViews = personalViewsByParent[tableView.publicId] || []
          const row = (view: ITableViewWithColumns, indented: boolean) => (
            <ViewRow
              key={view.publicId}
              view={view}
              inProcess={inProcess}
              isSelected={view.publicId === spreadsheetData.viewDetails.publicId}
              isEditing={editingViewId === view.publicId}
              editedName={editedName}
              indented={indented}
              editInputRef={editInputRef}
              anyRowHydrating={hydratingViewId !== null}
              onSelect={() => selectView(view.publicId)}
              onStartEdit={() => startEditing(view.publicId, view.name)}
              onSaveEdit={() => saveViewName(view.publicId)}
              onCancelEdit={cancelEdit}
              onEditNameChange={setEditedName}
              onDelete={() => handleDeleteView(view.publicId, view.name)}
              canEdit={editable && canEditView(view)}
            />
          )
          return (
            <React.Fragment key={tableView.publicId}>
              {row(tableView, false)}
              {/* Personal views sit under the view they were made from. */}
              {childViews.map((childView: ITableViewWithColumns) => row(childView, true))}
            </React.Fragment>
          )
        })}

        {noResults && <div className="px-2 py-1.5 text-[13px] text-muted select-none">No results</div>}
      </div>
    </>
  )

  const createItems = [
    /* Grid-layout views (spreadsheet, transpose, matrix) come first — they all
          render rows × columns and feel like variants of one another. */
    allowPersonalViews &&
      wrapIfBlocked(
        <MenuItem
          icon={<Spreadsheet />}
          data-tour="new-spreadsheet-view"
          onClick={() => createNewView(ViewTypes.SPREADSHEET)}
          loading={isCreatingNewViewType === ViewTypes.SPREADSHEET}
          disabled={isCreatingNewViewType !== null || personalViewsDisabled}
        >
          {inProcess ? 'New Personal Spreadsheet View' : 'New Spreadsheet View'}
        </MenuItem>
      ),

    allowPersonalViews &&
      wrapIfBlocked(
        <MenuItem
          icon={<TransposeView />}
          onClick={() => createNewView(ViewTypes.TRANSPOSE)}
          loading={isCreatingNewViewType === ViewTypes.TRANSPOSE}
          disabled={isCreatingNewViewType !== null || personalViewsDisabled}
        >
          {inProcess ? 'New Personal Transpose View' : 'New Transpose View'}
          <BetaBadge />
        </MenuItem>
      ),

    allowPersonalViews &&
      wrapIfBlocked(
        <MenuItem
          icon={<MatrixView />}
          data-tour="new-matrix-view"
          onClick={() => createNewView(ViewTypes.MATRIX)}
          loading={isCreatingNewViewType === ViewTypes.MATRIX}
          disabled={isCreatingNewViewType !== null || personalViewsDisabled}
        >
          {inProcess ? 'New Personal Matrix View' : 'New Matrix View'}
          <BetaBadge />
        </MenuItem>
      ),

    /* Non-grid views (form, chart, calendar) — different presentation models. */
    !inProcess &&
      wrapIfBlocked(
        <MenuItem
          icon={<Form />}
          onClick={() => createNewView(ViewTypes.FORM)}
          loading={isCreatingNewViewType === ViewTypes.FORM}
          disabled={isCreatingNewViewType !== null || personalViewsDisabled}
        >
          New Form View
        </MenuItem>
      ),

    allowPersonalViews &&
      wrapIfBlocked(
        <MenuItem
          icon={<Chart />}
          onClick={() => createNewView(ViewTypes.CHART)}
          loading={isCreatingNewViewType === ViewTypes.CHART}
          disabled={isCreatingNewViewType !== null || personalViewsDisabled}
        >
          {inProcess ? 'New Personal Chart View' : 'New Chart View'}
        </MenuItem>
      ),

    allowPersonalViews &&
      wrapIfBlocked(
        <MenuItem
          icon={<CalendarView />}
          onClick={() => createNewView(ViewTypes.CALENDAR)}
          loading={isCreatingNewViewType === ViewTypes.CALENDAR}
          disabled={isCreatingNewViewType !== null || personalViewsDisabled}
        >
          {inProcess ? 'New Personal Calendar View' : 'New Calendar View'}
        </MenuItem>
      ),
    // A Custom View, built by MortaAI from a prompt, closes the list: owner-authored and never personal.
    spreadsheetData.isAdmin && !inProcess && (
      <>
        <MenuSeparator />
        <MenuItem
          icon={VIEW_ROW_PRESENTATION[ViewTypes.CUSTOM_VIEW].icon}
          description="Dashboards, forms, trackers"
          onClick={() => createNewView(ViewTypes.CUSTOM_VIEW)}
          loading={isCreatingNewViewType === ViewTypes.CUSTOM_VIEW}
          disabled={isCreatingNewViewType !== null}
        >
          New custom view with <span className="text-blue">MortaAI</span>
        </MenuItem>
      </>
    )
  ]
    .filter(Boolean)
    // Keyed out here: a blocked row arrives wrapped in its tooltip.
    .map((item, index) => <React.Fragment key={index}>{item}</React.Fragment>)

  const content = () => {
    if (createOnly)
      return (
        <MenuList>
          <MenuLabel>Create</MenuLabel>
          {createItems}
        </MenuList>
      )

    if (!aboutView) {
      // The list of views, then creating one: on the table page in a New View
      // submenu (+ New view, beside the tabs, has them too); in a document, which
      // has no New view button, under Create.
      return (
        <>
          {/* Renaming and deleting are on a view's tab (its ⋯, or a right-click); a
              document has no tabs, so there its list keeps them. */}
          {viewList({ editable: inProcess, searchable: true })}
          {createItems.length > 0 && (
            <MenuList className="border-t border-divider">
              {inProcess ? (
                <>
                  <MenuLabel>Create</MenuLabel>
                  {createItems}
                </>
              ) : (
                <MenuSub label="New View" icon={<Plus />} width={260} data-testid="new-view">
                  <MenuList>
                    <MenuLabel>Create</MenuLabel>
                    {createItems}
                  </MenuList>
                </MenuSub>
              )}
            </MenuList>
          )}
        </>
      )
    }

    return (
      <>
        {/* The view's own settings, under a header pinned as Table settings' is. */}
        {hasSettings && <MenuHeader>View settings</MenuHeader>}
        <MenuList>
          {hasSettings && (
            <>
              <div role="group" aria-label="This view" className="flex flex-col">
                {canRenameSectionView && (
                  <MenuItem
                    icon={<Pencil />}
                    onClick={() => {
                      setMenuState({ ...INITIAL_CONTEXT_MENU_STATE })
                      onRenameView?.(sectionView.publicId)
                    }}
                  >
                    Rename View
                  </MenuItem>
                )}
                <ViewSettingsItems view={sectionView} />
              </div>
              {(showSubmenus || canDeleteSectionView) && <MenuSeparator />}
            </>
          )}
          {showSubmenus && (
            <>
              <MenuSub
                label="Switch View"
                icon={<SwitchView />}
                width={VIEW_LIST_MAX_WIDTH}
                minWidth={VIEW_LIST_MIN_WIDTH}
                data-testid="switch-view"
              >
                {viewList({ editable: false, searchable: viewCount > 7 })}
              </MenuSub>
              {createItems.length > 0 && (
                <MenuSub label="New View" icon={<Plus />} width={260} data-testid="new-view">
                  <MenuList>
                    <MenuLabel>Create</MenuLabel>
                    {createItems}
                  </MenuList>
                </MenuSub>
              )}
            </>
          )}
          {canDeleteSectionView && (
            <>
              {showSubmenus && <MenuSeparator />}
              <MenuItem
                variant="destructive"
                icon={<Delete />}
                disabled={sectionView.isDefault}
                onClick={() => handleDeleteView(sectionView.publicId, sectionView.name)}
              >
                Delete View
              </MenuItem>
            </>
          )}
        </MenuList>
        {canDeleteSectionView && sectionView.isDefault && (
          <MenuFooter>
            This is the table&apos;s default view. It opens first for everyone, so it can&apos;t be deleted.
          </MenuFooter>
        )}
      </>
    )
  }

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width} minWidth={minWidth}>
      {content()}
    </Menu>
  )
}

export default ViewMenu
